<template>
  <div class="h-screen overflow-y-auto bg-slate-100 p-4 space-y-4">
    <!-- 筛选区域 -->
    <div class="mx-auto bg-white rounded-xl shadow-md border border-slate-200 mb-4 max-w-7xl transition-all duration-300">
      <div class="p-6">
        <!-- 标题和批量操作按钮 -->
        <div class="flex justify-between items-center mb-4">
          <h2 class="text-lg font-semibold text-slate-800">{{ t('user_mgmt_title') }}</h2>
          <div class="flex items-center gap-4">
            <button
              class="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500"
              @click="toggleBatchMode"
            >
              {{ isBatchMode ? t('exit_batch_mode') : t('enter_batch_mode') }}
            </button>
            <!-- 全选按钮，仅在批量模式下显示 -->
            <button
              v-if="isBatchMode"
              class="px-4 py-2 bg-gray-500 text-white rounded-md hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-gray-500"
              @click="toggleSelectAll"
            >
              {{ isAllSelected ? t('deselect_all') : t('select_all') }}
            </button>
            <!-- 批量解绑按钮 -->
            <button
              v-if="isBatchMode"
              class="px-4 py-2 bg-red-500 text-white rounded-md hover:bg-red-600 focus:outline-none focus:ring-2 focus:ring-red-500"
              @click="handleBatchUnbind"
              :disabled="!selectedUsers.length"
            >
              {{ t('batch_unbind') }}
            </button>
            <!-- 下载报告按钮，仅在批量模式下显示 -->
            <button
              v-if="isBatchMode"
              class="px-4 py-2 bg-green-500 text-white rounded-md hover:bg-green-600 focus:outline-none focus:ring-2 focus:ring-green-500"
              @click="prepareDownloadReports"
              :disabled="!selectedUsers.length"
            >
              {{ t('download_report') }}
            </button>
          </div>
        </div>
        
        <!-- 筛选区域 -->
        <div class="space-y-4">
          <!-- 搜索框 -->
          <div class="flex items-center gap-3">
            <span class="text-slate-600 font-medium min-w-[80px]">{{ t('search_colon') }}</span>
            <div class="relative flex-1 max-w-xl">
              <input
                v-model="searchQuery"
                type="text"
                :placeholder="t('search_user_phone_device')"
                class="w-full px-4 py-2.5 bg-white border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none shadow-sm text-base"
                @input="handleSearch"
              />
              <svg 
                v-if="!searchQuery"
                class="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400"
                fill="none" 
                stroke="currentColor" 
                viewBox="0 0 24 24"
              >
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              <button
                v-else
                @click="clearSearch"
                class="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400 hover:text-slate-600 transition-colors"
              >
                <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>

          <!-- 设备状态筛选 -->
          <div class="flex items-center gap-3">
            <span class="text-slate-600 font-medium min-w-[80px]">{{ t('device_status') }}</span>
            <el-radio-group v-model="deviceBindStatus" @change="handleDeviceStatusChange" class="flex-1">
              <el-radio-button label="" class="!rounded-l-lg">{{ t('all') }}</el-radio-button>
              <el-radio-button label="bound">{{ t('bound_device') }}</el-radio-button>
              <el-radio-button label="unbound" class="!rounded-r-lg">{{ t('unbound_device') }}</el-radio-button>
            </el-radio-group>
          </div>
        </div>
      </div>
    </div>

    <!-- 用户列表 -->
    <div class="mx-auto max-w-7xl bg-white rounded-lg shadow-md border border-slate-200">
      <div class="p-6">
        <!-- 用户列表表格 -->
        <el-table 
          :data="rows" 
          style="width: 100%" 
          border 
          class="custom-table"
          :max-height="tableMaxHeight"
          @selection-change="handleSelectionChange"
        >
          <!-- 选择列，仅在批量模式下显示 -->
          <el-table-column
            v-if="isBatchMode"
            type="selection"
            width="55"
            :selectable="row => !!row.device_id"
          />
          <!-- 用户名列 -->
          <el-table-column :label="t('username')" min-width="120">
            <template #default="scope">
              <div class="flex items-center gap-2">
                <span 
                  v-if="canJumpToUserReport"
                  class="font-medium cursor-pointer hover:text-blue-600 transition-colors"
                  :class="scope.row.nickname ? 'text-slate-700' : 'text-slate-400 italic'"
                  @click="handleUserClick(scope.row)"
                >
                  {{ scope.row.nickname || t('no_username') }}
                </span>
                <span 
                  v-else
                  class="font-medium"
                  :class="scope.row.nickname ? 'text-slate-700' : 'text-slate-400 italic'"
                >
                  {{ scope.row.nickname || t('no_username') }}
                </span>
              </div>
            </template>
          </el-table-column>

          <!-- 手机号列 -->
          <el-table-column :label="t('phone_number')" min-width="140">
            <template #default="scope">
              <span 
                v-if="canJumpToUserReport"
                class="cursor-pointer hover:text-blue-600 transition-colors"
                :class="scope.row.phone_number ? 'text-slate-600' : 'text-slate-400 italic'"
                @click="handleUserClick(scope.row)"
              >
                {{ scope.row.phone_number || t('no_phone') }}
              </span>
              <span 
                v-else
                :class="scope.row.phone_number ? 'text-slate-600' : 'text-slate-400 italic'"
              >
                {{ scope.row.phone_number || t('no_phone') }}
              </span>
            </template>
          </el-table-column>

          <!-- 设备绑定列 -->
          <el-table-column :label="t('bind_device')" min-width="180">
            <template #default="scope">
              <div class="flex items-center gap-2">
                <div class="flex items-center gap-2">
                  <span 
                    :class="[
                      'px-2 py-1 rounded-md text-sm font-medium cursor-pointer hover:bg-opacity-75 transition-colors',
                      scope.row.device_id 
                        ? 'bg-green-50 text-green-600 hover:bg-green-100' 
                        : 'bg-slate-50 text-slate-600'
                    ]"
                    @click="handleDeviceClick(scope.row.device_id)"
                  >
                    {{ scope.row.device_id || t('unbound_device') }}
                  </span>
                  <el-button
                    v-if="scope.row.device_id"
                    @click="handleUnbind(scope.row)"
                    type="danger"
                    size="small"
                    :loading="unbindLoading"
                    class="!flex !items-center !gap-1"
                  >
                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
                    </svg>
                    {{ t('unbind') }}
                  </el-button>
                </div>
              </div>
            </template>
          </el-table-column>

          <!-- 基本信息列 -->
          <el-table-column :label="t('basic_info')" min-width="280">
            <template #default="scope">
              <div class="grid grid-cols-2 gap-3">
                <div class="flex items-center gap-2">
                  <span class="text-slate-500">{{ t('gender_colon') }}</span>
                  <span :class="[
                    'px-2 py-0.5 rounded text-sm',
                    scope.row.sex 
                      ? 'bg-blue-50 text-blue-600 font-medium' 
                      : 'bg-slate-50 text-slate-400 italic'
                  ]">
                    {{ displayGender(scope.row.sex) }}
                  </span>
                </div>
                <div class="flex items-center gap-2">
                  <span class="text-slate-500">{{ t('birthday_colon') }}</span>
                  <span :class="scope.row.birthdate ? 'font-medium text-slate-700' : 'text-slate-400 italic'">
                    {{ scope.row.birthdate || t('not_set') }}
                  </span>
                </div>
                <div class="flex items-center gap-2">
                  <span class="text-slate-500">{{ t('height_colon') }}</span>
                  <span :class="scope.row.height ? 'font-medium text-slate-700' : 'text-slate-400 italic'">
                    {{ scope.row.height ? `${scope.row.height}cm` : t('not_set') }}
                  </span>
                </div>
                <div class="flex items-center gap-2">
                  <span class="text-slate-500">{{ t('weight_colon') }}</span>
                  <span :class="scope.row.weight ? 'font-medium text-slate-700' : 'text-slate-400 italic'">
                    {{ scope.row.weight ? `${scope.row.weight}kg` : t('not_set') }}
                  </span>
                </div>
                <div class="flex items-center gap-2 col-span-2">
                  <span class="text-slate-500">BMI:</span>
                  <span :class="[
                    'px-2 py-0.5 rounded font-medium',
                    getBMIColorClass(calculateBMI(scope.row.height, scope.row.weight))
                  ]">
                    {{ calculateBMI(scope.row.height, scope.row.weight) }}
                  </span>
                </div>
              </div>
            </template>
          </el-table-column>

          <!-- 操作列 -->
          <el-table-column :label="t('actions')" width="100" fixed="right">
            <template #default="scope">
              <el-button 
                @click="openEditDialog(scope.row)" 
                type="primary" 
                size="small"
                class="!flex !items-center !gap-1"
              >
                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                </svg>
                {{ t('edit') }}
              </el-button>
            </template>
          </el-table-column>
        </el-table>
      </div>
    </div>

    <!-- 分页 -->
    <div class="mx-auto max-w-7xl mt-6 flex justify-center p-4 bg-white rounded-lg shadow-sm border border-slate-200">
      <el-pagination
        v-model:current-page="currentPage"
        v-model:page-size="pageSize"
        :total="total"
        :page-sizes="[10, 20, 50, 100]"
        layout="total, sizes, prev, pager, next"
        @size-change="handleSizeChange"
        @current-change="handleCurrentChange"
        background
        class="!text-sm"
      />
    </div>

    <!-- 编辑对话框 -->
    <el-dialog
      v-model="editDialogVisible"
      :title="t('edit_user_info')"
      width="500px"
      class="custom-dialog"
    >
      <el-form :model="editForm" label-width="100px" class="add-user-form">
        <el-form-item :label="t('username')">
          <el-input v-model="editForm.username" :placeholder="t('please_input_username')" />
        </el-form-item>
        <el-form-item :label="t('phone_number')">
          <span class="text-slate-600">{{ editForm.user }}</span>
        </el-form-item>
        <el-form-item :label="t('gender')">
          <el-select v-model="editForm.sex" :placeholder="t('please_select_gender')" class="!w-full">
            <el-option :label="t('gender_male')" value="男" />
            <el-option :label="t('gender_female')" value="女" />
          </el-select>
        </el-form-item>
        <el-form-item :label="t('birthday')">
          <el-date-picker
            v-model="editForm.birthdate"
            type="month"
            :placeholder="t('select_birthday')"
            format="YYYY-MM"
            value-format="YYYY-MM"
            class="!w-full"
          />
        </el-form-item>
        <el-form-item :label="t('height')">
          <el-input-number 
            v-model="editForm.height"
            :min="0"
            :max="300"
            :step="1"
            class="!w-[180px]"
          >
            <template #suffix>cm</template>
          </el-input-number>
        </el-form-item>
        <el-form-item :label="t('weight')">
          <el-input-number 
            v-model="editForm.weight"
            :min="0"
            :max="500"
            :step="1"
            class="!w-[180px]"
          >
            <template #suffix>kg</template>
          </el-input-number>
        </el-form-item>
        <el-form-item label="BMI">
          <span :class="[
            'px-2 py-0.5 rounded font-medium',
            getBMIColorClass(calculateBMI(editForm.height, editForm.weight))
          ]">
            {{ calculateBMI(editForm.height, editForm.weight) }}
          </span>
        </el-form-item>
      </el-form>
      <template #footer>
        <div class="flex justify-end gap-3">
          <el-button @click="editDialogVisible = false" class="!px-6">{{ t('cancel') }}</el-button>
          <el-button type="primary" @click="handleSubmit" class="!px-6">{{ t('ok') }}</el-button>
        </div>
      </template>
    </el-dialog>

    <!-- 日期选择弹窗 -->
    <el-dialog
      v-model="showDatePicker"
      :title="t('select_date_range')"
      width="30%"
      destroy-on-close
    >
      <div class="flex flex-col items-center">
        <!-- 添加提示信息 -->
        <div class="w-full mb-4 p-3 bg-blue-50 text-blue-700 rounded-md">
          <div class="flex items-start">
            <svg class="w-5 h-5 mr-2 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span>
              {{ t('download_range_hint') }}
            </span>
          </div>
        </div>
        
        <el-date-picker
          v-model="dateRange"
          type="daterange"
          range-separator="至"
          start-:placeholder="t('start_date')"
          end-:placeholder="t('end_date')"
          format="YYYY-MM-DD"
          value-format="YYYY-MM-DD"
          class="w-full mb-4"
        />
        <div class="flex justify-end w-full mt-4">
          <el-button @click="showDatePicker = false" class="mr-2">{{ t('cancel') }}</el-button>
          <el-button type="primary" @click="confirmDownload">{{ t('confirm_download') }}</el-button>
        </div>
      </div>
    </el-dialog>
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted, computed } from 'vue';
import { t } from '@/plugins/utils/i18n';
import { useStore } from 'vuex'
import { useRouter } from 'vue-router';
import { createAxiosInstance, request_data } from "@/plugins/axios.js";
import { getOAHIDevices, isOAHIDevice as checkIsOAHIDevice } from '@/plugins/deviceConfig.js'
import { ElMessage, ElMessageBox, ElLoading } from 'element-plus';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';

const router = useRouter();
const store = useStore();

// 性别显示映射（后端返 '男'/'女' / 'M'/'F' / '1'/'0' / 空 均兼容，与 OrgReportDetail.vue 对齐）
function displayGender(raw) {
  if (!raw) return t('not_set');
  const s = String(raw).trim().toLowerCase();
  if (s === '男' || s === 'm' || s === 'male' || s === '1') return t('gender_male');
  if (s === '女' || s === 'f' || s === 'female' || s === '0') return t('gender_female');
  return raw;
}

// ─── 响应式状态 ───────────────────────────────────────────

const currentPage = ref(1);
const pageSize = ref(10);
const total = ref(0);
const rows = ref([]);
const searchQuery = ref('');
const searchDebounceTimer = ref(null);
const deviceBindStatus = ref('');
const unbindLoading = ref(false);
const tableMaxHeight = ref(500);

const user = computed(() => store.state.user);
const org_bind = computed(() => user.value.org);

// 判断是否是干预枕机构
const isPillowOrg = computed(() => {
  const userOrg = localStorage.getItem('userOrg')
  return userOrg === '干预枕'
})

// MVP3被试摸底：也需要支持"按用户(openid)查看报告"
const isMvp3Org = computed(() => {
  const userOrg = localStorage.getItem('userOrg')
  return userOrg === 'MVP3被试摸底'
})

// 允许点击跳转到用户专属报告页的机构
const canJumpToUserReport = computed(() => isPillowOrg.value || isMvp3Org.value)

// ─── 数据获取（users_v2） ─────────────────────────────────

const fetchData = async () => {
  try {
    const params = new URLSearchParams();
    params.set('user_org', org_bind.value);
    params.set('page', currentPage.value);
    params.set('pagesize', pageSize.value);

    if (deviceBindStatus.value) {
      params.set('bind_status', deviceBindStatus.value);
    }
    if (searchQuery.value.trim()) {
      params.set('search', searchQuery.value.trim());
    }

    const response = await request_data(`/users/v2/?${params.toString()}`);

    // request_data 已解包两层（response.data.data），实际拿到 {nodes: [...], count: N}
    if (response && response.nodes) {
      rows.value = response.nodes;
      total.value = response.count || 0;
    } else {
      console.error('未知的响应数据结构:', response);
      rows.value = [];
      total.value = 0;
    }
  } catch (error) {
    console.error('获取数据失败:', error);
    ElMessage.error(t('fetch_data_failed'));
  }
};

// ─── 搜索 & 筛选 ─────────────────────────────────────────

const handleSearch = () => {
  if (searchDebounceTimer.value) {
    clearTimeout(searchDebounceTimer.value)
  }
  searchDebounceTimer.value = setTimeout(() => {
    currentPage.value = 1;
    fetchData()
  }, 300)
};

const clearSearch = () => {
  searchQuery.value = '';
  currentPage.value = 1;
  fetchData();
};

const handleDeviceStatusChange = () => {
  currentPage.value = 1;
  fetchData();
};

// ─── 分页 ─────────────────────────────────────────────────

const handleSizeChange = (newSize) => {
  pageSize.value = newSize;
  fetchData();
};

const handleCurrentChange = (val) => {
  currentPage.value = val;
  fetchData();
};

// ─── BMI ──────────────────────────────────────────────────

const calculateBMI = (height, weight) => {
  if (!height || !weight || isNaN(height) || isNaN(weight)) return '-';
  const heightInMeters = height / 100;
  const bmi = (weight / (heightInMeters * heightInMeters)).toFixed(1);
  return isNaN(bmi) ? '-' : bmi;
};

const getBMIColorClass = (bmi) => {
  if (bmi === '-') return 'bg-slate-50 text-slate-500';
  const bmiValue = parseFloat(bmi);
  if (bmiValue < 18.5) return 'bg-yellow-50 text-yellow-600';
  if (bmiValue >= 18.5 && bmiValue < 24) return 'bg-green-50 text-green-600';
  if (bmiValue >= 24 && bmiValue < 28) return 'bg-orange-50 text-orange-600';
  return 'bg-red-50 text-red-600';
};

// ─── 编辑用户 ─────────────────────────────────────────────

const editDialogVisible = ref(false);
const editForm = ref({
  user_id: null,
  username: null,
  user: null,
  sex: null,
  birthdate: null,
  height: null,
  weight: null,
  device_id: null,
  openid: null,
});

const openEditDialog = (rowData) => {
  // V2: 列表数据已包含所有字段，无需再调 /userinfo/
  editForm.value = {
    user_id: rowData.user_id || null,
    username: rowData.nickname || null,
    user: rowData.phone_number || null,
    sex: rowData.sex || null,
    birthdate: rowData.birthdate || null,
    height: Number(rowData.height) || null,
    weight: Number(rowData.weight) || null,
    device_id: rowData.device_id || null,
    openid: rowData.openid || null,
  };
  editDialogVisible.value = true;
};

const handleSubmit = async () => {
  try {
    const userId = editForm.value.user_id;
    if (!userId) {
      ElMessage.error(t('missing_user_id'));
      return;
    }
    const path = `/users/v2/${encodeURIComponent(userId)}/`;
    const axiosInstance = createAxiosInstance("数据服务器");

    const requestBody = {
      nickname: editForm.value.username || '',
      sex: editForm.value.sex || '',
      height: (editForm.value.height || '').toString(),
      weight: (editForm.value.weight || '').toString(),
      birthdate: editForm.value.birthdate || '',
    };

    const response = await axiosInstance.put(path, requestBody, {
      headers: { 'Content-Type': 'application/json' }
    });
    if (response.data?.success !== true) {
      throw new Error(response.data?.message || t('update_failed'));
    }

    editDialogVisible.value = false;
    ElMessage.success(t('user_update_success'));
    fetchData();
  } catch (error) {
    console.error('更新失败:', error);
    ElMessage.error(error.response?.data?.detail || error.message || t('update_failed'));
  }
};

// ─── 用户点击（跳转报告页） ───────────────────────────────

const handleUserClick = (row) => {
  if (!canJumpToUserReport.value) return;

  const openid = row.openid;
  if (!openid) {
    ElMessage.warning(t('user_no_openid'));
    return;
  }

  router.push({
    name: 'UserReportPillow',
    params: { openid }
  });
};

// ─── 解绑（单个） ────────────────────────────────────────

const handleUnbind = async (row) => {
  try {
    await ElMessageBox.confirm(t('confirm_unbind'), '提示', {
      confirmButtonText: '确定',
      cancelButtonText: '取消',
      type: 'warning'
    });

    unbindLoading.value = true;

    const axiosInstance = createAxiosInstance("数据服务器");
    await axiosInstance.post(`/users/v2/${encodeURIComponent(row.user_id)}/unbind/`, {
      device_id: row.device_id,
    }, {
      headers: { 'Content-Type': 'application/json' }
    });

    // 等待后端同步
    await new Promise(resolve => setTimeout(resolve, 2000));

    ElMessage.success(t('unbind_success'));
    fetchData();
  } catch (error) {
    if (error !== 'cancel') {
      console.error('解绑失败:', error);
      ElMessage.error(t('unbind_failed'));
    }
  } finally {
    unbindLoading.value = false;
  }
};

// ─── 设备点击（跳转设备页） ───────────────────────────────

const handleDeviceClick = (deviceID) => {
  if (!deviceID) return;
  router.push({
    name: 'Devices',
    query: { deviceID }
  });
};

// ─── 表格高度自适应 ──────────────────────────────────────

const calculateTableHeight = () => {
  const reservedSpace = 300;
  tableMaxHeight.value = window.innerHeight - reservedSpace;
};

// ─── 批量操作 ─────────────────────────────────────────────

const isBatchMode = ref(false);
const isAllSelected = ref(false);
const selectedUsers = ref([]);
const dateRange = ref([]);
const showDatePicker = ref(false);

const toggleBatchMode = () => {
  isBatchMode.value = !isBatchMode.value;
  if (!isBatchMode.value) {
    isAllSelected.value = false;
    selectedUsers.value = [];
  }
};

const toggleSelectAll = () => {
  const table = document.querySelector('.el-table');
  if (table) {
    const toggleCheckbox = table.querySelector('.el-table__header-wrapper .el-checkbox');
    if (toggleCheckbox) {
      toggleCheckbox.click();
    }
  }
};

const handleSelectionChange = (selection) => {
  selectedUsers.value = selection;
  isAllSelected.value = selection.length === rows.value.length && rows.value.length > 0;
};

// ─── 批量解绑 ─────────────────────────────────────────────

const batchRequest = async (items, fn, batchSize = 5) => {
  const results = [];
  for (let i = 0; i < items.length; i += batchSize) {
    const batch = items.slice(i, i + batchSize);
    const batchResults = await Promise.all(batch.map(fn));
    results.push(...batchResults);
  }
  return results;
};

const handleBatchUnbind = async () => {
  if (selectedUsers.value.length === 0) {
    ElMessage.warning(t('please_select_unbind_user'));
    return;
  }

  const usersToUnbind = selectedUsers.value.filter(u => u.device_id);

  if (usersToUnbind.length === 0) {
    ElMessage.warning(t('selected_no_bound_users'));
    return;
  }

  try {
    await ElMessageBox.confirm(
      `确定要解绑选中的 ${usersToUnbind.length} 个用户的设备吗？`,
      '批量解绑确认',
      {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }
    );

    let completedCount = 0;
    const totalCount = usersToUnbind.length;

    const loadingInstance = ElLoading.service({
      lock: true,
      text: `正在批量解绑... (0/${totalCount})`,
      background: 'rgba(0, 0, 0, 0.7)'
    });

    try {
      await batchRequest(usersToUnbind, async (u) => {
        const axiosInstance = createAxiosInstance("数据服务器");
        await axiosInstance.post(`/users/v2/${encodeURIComponent(u.user_id)}/unbind/`, {
          device_id: u.device_id,
        }, {
          headers: { 'Content-Type': 'application/json' }
        });

        completedCount++;
        loadingInstance.text = `正在批量解绑... (${completedCount}/${totalCount})`;
      }, 5);

      loadingInstance.close();
      ElMessage.success(`成功解绑 ${usersToUnbind.length} 个设备`);
      await fetchData();
      selectedUsers.value = [];
    } catch (error) {
      loadingInstance.close();
      throw error;
    }
  } catch (error) {
    if (error !== 'cancel') {
      console.error('批量解绑失败:', error);
      ElMessage.error(t('batch_unbind_failed'));
    }
  }
};

// ─── 批量下载报告 ─────────────────────────────────────────

const prepareDownloadReports = () => {
  if (selectedUsers.value.length === 0) {
    ElMessage.warning(t('please_select_at_least_one_user'));
    return;
  }
  showDatePicker.value = true;
};

const confirmDownload = async () => {
  showDatePicker.value = false;
  await downloadReports();
};

const downloadReports = async () => {
  // V2: 直接从行数据取 device_id
  const devices = selectedUsers.value
    .map(u => u.device_id)
    .filter(Boolean);

  if (devices.length === 0) {
    ElMessage.warning(t('selected_no_bound_devices'));
    return;
  }

  try {
    const axiosInstance = createAxiosInstance("API服务器");

    // 计算日期范围
    let days = 365;
    let endDate = null;
    if (dateRange.value && dateRange.value[0] && dateRange.value[1]) {
      const startDate = new Date(dateRange.value[0]);
      const endDateObj = new Date(dateRange.value[1]);
      const diffTime = Math.abs(endDateObj - startDate);
      days = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
      endDate = dateRange.value[1];
    }

    ElMessage.info(t('fetching_report_data'));
    const response = await axiosInstance.post('/api/v1/orgs/reports/trend/batch', {
      devices: devices,
      days: days,
      end_date: endDate
    });

    // 为每个设备获取用户信息（用于 Excel 导出）
    const deviceUserInfoMap = new Map();
    const uniqueDevices = [...new Set(devices)];

    ElMessage.info(t('fetching_user_info'));
    for (const deviceId of uniqueDevices) {
      try {
        // 优先从已有的 selectedUsers 列表中查找
        const userRow = selectedUsers.value.find(u => u.device_id === deviceId);
        if (userRow) {
          deviceUserInfoMap.set(deviceId, {
            username: userRow.nickname || '',
            user: userRow.phone_number || '',
            height: userRow.height || 0,
            sex: userRow.sex || '',
            weight: userRow.weight || 0,
            birthdate: userRow.birthdate || ''
          });
        } else {
          // fallback: 通过 /devices/v2/ 接口查询设备绑定的用户信息
          const devData = await request_data(`/devices/v2/?search=${encodeURIComponent(deviceId)}&pagesize=1`, '数据服务器').catch(() => null);
          const devNode = devData?.nodes?.[0];
          deviceUserInfoMap.set(deviceId, {
            username: devNode?.nickname || '',
            user: devNode?.phone_number || '',
            height: devNode?.height || 0,
            sex: devNode?.sex || '',
            weight: devNode?.weight || 0,
            birthdate: devNode?.birthdate || ''
          });
        }
      } catch (error) {
        console.warn(`获取设备 ${deviceId} 的用户信息失败:`, error);
        deviceUserInfoMap.set(deviceId, {
          username: '', user: '', height: 0, sex: '', weight: 0, birthdate: ''
        });
      }
    }

    const rawData = [];
    const batchData = response.data?.data || {};
    for (const [deviceId, deviceData] of Object.entries(batchData)) {
      const reports = deviceData.reports || [];
      const userInfo = deviceUserInfoMap.get(deviceId) || {
        username: '', user: '', height: 0, sex: '', weight: 0, birthdate: ''
      };

      for (const report of reports) {
        rawData.push({
          device: deviceId,
          date: report.date,
          reports: {
            online: {
              tst: report.tst,
              tib: report.tib,
              sol: report.sol,
              waso: report.waso,
              deep_time: report.deep_time,
              rem_time: report.rem_time,
              sri: report.sri,
              sfi: report.sfi,
              sleep_dur_1: report.sleep_dur_1,
              ahi: report.ahi,
              ahi_345: report.ahi_345,
              ahi_2: report.ahi_2,
              avg_resp: report.avg_resp,
              // 关灯/开灯时间 = 入睡意图时间段，与详情页和客户口径一致
              sleep_intention_time: report.sleep_intention_time,
              intention_leave_time: report.intention_leave_time,
              rem_latency: report.rem_latency,
              core_time: report.core_time,
              bcgsnore_index: report.bcgsnore_index,
              hrv_ratio_mean: report.avg_hrv_radio,
              hrv_rmssd_mean: report.hrv_rmssd_mean
            },
            summmary: {
              rem_sleep_time: report.rem_time,
              rem_efficiency: report.rem_efficiency,
              core_sleep_time: report.core_time,
              core_sleep_efficiency: report.core_sleep_efficiency,
              deep_sleep_time: report.deep_time,
              deep_sleep_efficiency: report.deep_sleep_efficiency,
              min_sleep_heart_rate: report.bpm_min,
              avg_sleep_heart_rate: report.bpm_avg,
              avg_hrv_rmssd: report.avg_hrv_rmssd,
              avg_hrv_radio: report.avg_hrv_radio,
              high_illuminance: report.high_illu,
              avg_temperature: report.avg_temperature,
              avg_humidity: report.avg_humidity,
              username: userInfo.username,
              user: userInfo.user,
              height: userInfo.height,
              sex: userInfo.sex,
              weight: userInfo.weight,
              birthdate: userInfo.birthdate
            }
          }
        });
      }
    }

    console.log('📊 [批量下载] 原始数据示例（前3条）:', rawData.slice(0, 3).map(r => ({
      device: r.device,
      date: r.date,
      'online.hrv_ratio_mean': r.reports?.online?.hrv_ratio_mean,
      'summmary.avg_hrv_radio': r.reports?.summmary?.avg_hrv_radio
    })));
    const processedData = await processData(rawData);
    saveToExcel(processedData);
    ElMessage.success(t('report_download_success'));
  } catch (error) {
    console.error('下载报告失败:', error);
    ElMessage.error(t('download_report_failed_retry'));
  }
};

// ─── Excel 数据处理 & 导出 ────────────────────────────────

// OAHI 设备列表缓存
const oahiDevicesCache = ref([])

const initOAHIDevices = async () => {
  try {
    oahiDevicesCache.value = await getOAHIDevices()
    console.log('UserManagement - 初始化OAHI设备列表:', oahiDevicesCache.value)
  } catch (error) {
    console.error('UserManagement - 初始化 OAHI 设备列表失败:', error)
    oahiDevicesCache.value = []
  }
}

const isOAHIDevice = (deviceId) => {
  return oahiDevicesCache.value.includes(deviceId)
}

const formatTimestampToDatetime = (timestamp) => {
  // 用本地时区格式化（之前用 toISOString 会输出 UTC，导致东八区少显示 8h）
  if (!timestamp) return '';
  const d = new Date(timestamp * 1000);
  if (isNaN(d.getTime())) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
};

const processData = async (data) => {
  const processedData = {};

  // 并行获取缺失的 hrv_ratio_mean
  const reportDetailPromises = data.map(async (record) => {
    try {
      if (!record.reports?.online?.hrv_ratio_mean) {
        const dateStr = record.date;
        let fullDate;
        if (dateStr.includes('/')) {
          const [month, day] = dateStr.split('/');
          const currentYear = new Date().getFullYear();
          fullDate = `${currentYear}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
        } else {
          fullDate = dateStr;
        }

        try {
          const reportDetail = await request_data(
            `/devicereport/${record.device}/${fullDate}/`,
            '计算服务器1'
          ).catch(() => null);

          if (reportDetail) {
            const hrvRatioMean = reportDetail.online?.hrv_ratio_mean ||
                                 reportDetail.final?.hrv_ratio_mean ||
                                 null;
            if (hrvRatioMean != null) {
              if (!record.reports) record.reports = {};
              if (!record.reports.online) record.reports.online = {};
              record.reports.online.hrv_ratio_mean = hrvRatioMean;
              console.log(`📊 [批量下载] 设备 ${record.device} 日期 ${record.date}: 从 /devicereport/ 获取 hrv_ratio_mean = ${hrvRatioMean}`);
            }
          }
        } catch (error) {
          console.warn(`⚠️ [批量下载] 获取设备 ${record.device} 日期 ${record.date} 的完整报告失败:`, error);
        }
      }
      return record;
    } catch (error) {
      console.warn(`⚠️ [批量下载] 处理记录失败:`, error);
      return record;
    }
  });

  const enrichedData = await Promise.all(reportDetailPromises);

  enrichedData.forEach(record => {
    const deviceId = record.device;
    if (!processedData[deviceId]) {
      processedData[deviceId] = [];
    }

    const useOAHI = isOAHIDevice(deviceId)
    const ahiValue = useOAHI ? (record.reports.online.oahi || record.reports.online.OAHI) : record.reports.online.ahi
    const nremAHI = useOAHI ? record.reports.online.oahi_345 : record.reports.online.ahi_345
    const remAHI = useOAHI ? record.reports.online.oahi_2 : record.reports.online.ahi_2
    const ahiLabel = useOAHI ? '阻塞性呼吸暂停低通气指数 (OAHI)' : '呼吸暂停低通指数 (AHI)'
    const nremLabel = useOAHI ? 'NREM OAHI' : 'NREM AHI'
    const remLabel = useOAHI ? 'REM OAHI' : 'REM AHI'

    const row = {
      "日期": record.date,
      "设备ID": record.device,
      "关灯时间": formatTimestampToDatetime(record.reports.online.sleep_intention_time),
      "开灯时间": formatTimestampToDatetime(record.reports.online.intention_leave_time),
      "总睡眠时长 (分钟)": record.reports.online.tst,
      "卧床时长 (分钟)": record.reports.online.tib,
      "入睡时间 (分钟)": record.reports.online.sol,
      "睡眠中断 (分钟)": record.reports.online.waso,
      "深度睡眠 (分钟)": record.reports.online.deep_time,
      "快速眼动潜伏期 (分钟)": record.reports.online.rem_latency,
      "睡眠规律性 (%)": record.reports.online.sri,
      "睡眠碎片化指数": record.reports.online.sfi,
      "清醒 (分钟)": record.reports.online.sleep_dur_1,
      "快速眼动睡眠 (分钟)": record.reports.online.rem_time,
      "浅度睡眠 (分钟)": record.reports.online.core_time,
      [ahiLabel]: ahiValue,
      [nremLabel]: nremAHI,
      [remLabel]: remAHI,
      "鼾声指数 (SI)": record.reports.online.bcgsnore_index,
      "平均呼吸率 (次/分)": record.reports.online.avg_resp,
      "最低心率 (次/分)": record.reports.summmary.min_sleep_heart_rate,
      "平均心率 (次/分)": record.reports.summmary.avg_sleep_heart_rate,
      "HRV_RMSSD (毫秒)": (() => {
        const hrvRmssd = record.reports?.online?.hrv_rmssd_mean != null
          ? parseFloat(record.reports.online.hrv_rmssd_mean)
          : (record.reports?.summmary?.avg_hrv_rmssd != null
              ? parseFloat(record.reports.summmary.avg_hrv_rmssd)
              : null);
        return hrvRmssd != null ? Math.round(hrvRmssd * 100) / 100 : null;
      })(),
      "HRV_LF/HF": (() => {
        const hrvRatio = record.reports?.online?.hrv_ratio_mean != null
          ? parseFloat(record.reports.online.hrv_ratio_mean)
          : (record.reports?.summmary?.avg_hrv_radio != null
              ? parseFloat(record.reports.summmary.avg_hrv_radio)
              : null);
        return hrvRatio != null ? Math.round(hrvRatio * 100) / 100 : null;
      })(),
      "平均温度 (°C)": record.reports.summmary.avg_temperature,
      "平均湿度 (%)": record.reports.summmary.avg_humidity,
      "手机号": record.reports.summmary.user,
      "姓名": record.reports.summmary.username,
      "身高": record.reports.summmary.height,
      "体重": record.reports.summmary.weight,
      "性别": record.reports.summmary.sex,
      "出生日期": record.reports.summmary.birthdate,
    };
    processedData[deviceId].push(row);
  });

  return processedData;
};

const saveToExcel = (processedData) => {
  console.log('📝 [Excel导出] 开始写入 Excel，数据概览:', {
    totalDevices: Object.keys(processedData).length,
    totalRecords: Object.values(processedData).reduce((sum, records) => sum + records.length, 0)
  });

  const workbook = XLSX.utils.book_new();
  for (const [deviceId, records] of Object.entries(processedData)) {
    console.log(`📝 [Excel导出] 处理设备 ${deviceId}，共 ${records.length} 条记录`);

    if (records.length > 0) {
      const sampleRecord = records[0];
      console.log(`📝 [Excel导出] 设备 ${deviceId} 第一条记录的 HRV_LF/HF:`, {
        value: sampleRecord['HRV_LF/HF'],
        type: typeof sampleRecord['HRV_LF/HF'],
      });
    }

    const worksheet = XLSX.utils.json_to_sheet(records);

    const range = XLSX.utils.decode_range(worksheet['!ref'] || 'A1');
    const header = Object.keys(records[0] || {});

    // 设置 HRV_LF/HF 列格式
    const hrvLfHfColumnIndex = header.indexOf('HRV_LF/HF');
    if (hrvLfHfColumnIndex >= 0) {
      const colLetter = XLSX.utils.encode_col(hrvLfHfColumnIndex);
      for (let row = 1; row <= range.e.r; row++) {
        const cellAddress = colLetter + (row + 1);
        if (worksheet[cellAddress]) {
          const cellValue = worksheet[cellAddress].v;
          if (cellValue != null && typeof cellValue !== 'number') {
            const numValue = parseFloat(cellValue);
            if (!isNaN(numValue)) {
              worksheet[cellAddress].v = numValue;
            }
          }
          worksheet[cellAddress].z = '0.00';
        }
      }
    }

    // 设置 HRV_RMSSD 列格式
    const hrvRmssdColumnIndex = header.indexOf('HRV_RMSSD (毫秒)');
    if (hrvRmssdColumnIndex >= 0) {
      const colLetter = XLSX.utils.encode_col(hrvRmssdColumnIndex);
      for (let row = 1; row <= range.e.r; row++) {
        const cellAddress = colLetter + (row + 1);
        if (worksheet[cellAddress]) {
          const cellValue = worksheet[cellAddress].v;
          if (cellValue != null && typeof cellValue !== 'number') {
            const numValue = parseFloat(cellValue);
            if (!isNaN(numValue)) {
              worksheet[cellAddress].v = numValue;
            }
          }
          worksheet[cellAddress].z = '0.00';
        }
      }
    }

    XLSX.utils.book_append_sheet(workbook, worksheet, `设备_${deviceId}`);
  }

  console.log('📝 [Excel导出] Excel 文件生成完成');
  const wbout = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
  saveAs(new Blob([wbout], { type: 'application/octet-stream' }), '睡眠报告_多设备.xlsx');
};

// ─── 生命周期 ─────────────────────────────────────────────

onMounted(async () => {
  await initOAHIDevices();
  fetchData();
  calculateTableHeight();
  window.addEventListener('resize', calculateTableHeight);
});

onUnmounted(() => {
  window.removeEventListener('resize', calculateTableHeight);
  if (searchDebounceTimer.value) {
    clearTimeout(searchDebounceTimer.value);
  }
});
</script>

<style scoped>
/* 表格样式 */
.custom-table :deep(.el-table__body-wrapper) {
  overflow-y: auto;
}

.custom-table {
  --el-table-border-color: theme('colors.slate.200');
  --el-table-header-bg-color: theme('colors.slate.50');
  --el-table-header-text-color: theme('colors.slate.700');
  --el-table-row-hover-bg-color: theme('colors.blue.50');
}

.custom-table :deep(.el-table__row) {
  border-bottom: 1px solid theme('colors.slate.100');
}

/* 单选按钮组样式 */
.el-radio-group :deep(.el-radio-button__inner) {
  @apply px-6 py-2.5 text-sm border-slate-200;
  height: auto;
  line-height: 1.5;
}

.el-radio-group :deep(.el-radio-button:first-child .el-radio-button__inner) {
  @apply border-r-0;
}

.el-radio-group :deep(.el-radio-button:last-child .el-radio-button__inner) {
  @apply border-l-0;
}

.el-radio-group :deep(.el-radio-button__original-radio:checked + .el-radio-button__inner) {
  @apply bg-blue-500 border-blue-500 text-white;
  box-shadow: -1px 0 0 0 #3b82f6;
}

/* 对话框样式 */
.custom-dialog :deep(.el-dialog__header) {
  @apply px-6 py-4 border-b border-slate-200;
}

.custom-dialog :deep(.el-dialog__title) {
  @apply text-lg font-semibold text-slate-800;
}

.custom-dialog :deep(.el-dialog__body) {
  @apply p-6;
}

.custom-dialog :deep(.el-dialog__footer) {
  @apply px-6 py-4 border-t border-slate-200;
}

/* 表单样式 */
.add-user-form :deep(.el-form-item__label) {
  @apply text-slate-700 font-medium;
}

.add-user-form :deep(.el-input__wrapper) {
  @apply shadow-none border border-slate-200 hover:border-blue-400;
}

.add-user-form :deep(.el-input__wrapper.is-focus) {
  @apply ring-2 ring-blue-500 border-transparent;
}

.add-user-form :deep(.el-input-number__wrapper) {
  @apply shadow-none border border-slate-200 hover:border-blue-400;
}

/* 添加设备ID的悬停样式 */
.device-id-link {
  @apply cursor-pointer transition-colors;
}

.device-id-link:hover {
  @apply bg-opacity-75;
}
</style>
